<script setup>
import CarCard from '@/components/cards/CarCard.vue';
defineProps({
  items: {
    type: Array,
    required: true,
    default: () => [],
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
  view: {
    type: String,
    required: true,
    default: 'default',
    validator: (value) => {
      return ['default', 'compact'].includes(value);
    },
  },
  asHeading: {
    type: Boolean,
    default: true,
  },
});
</script>
<template>
  <div :class="['cars__items', `cars__items--${view}`]">
    <template v-for="(item, itemIndex) in items" :key="item.title">
      <CarCard
        :data-aos="view === 'compact' ? '' : 'fade-up'"
        :data-aos-delay="
          view === 'compact' ? 0 : itemIndex >= 1 ? 100 + 100 * itemIndex : 100
        "
        :background-color="backgroundColor"
        :view="view"
        :url="item?.url"
        :in-stock="item?.in_stock"
        :image="item?.thumbnails || item?.image_cover || item?.brand"
        :empty-image="!item?.thumbnails && !item?.image_cover"
        :name="item?.title"
        :price="item?.pricing?.price_old"
        :discount="item?.pricing?.discount"
        :current-price="item?.pricing?.price_current"
        :currency="item?.pricing?.currency"
        :max-payment="item?.financing?.max_payment"
        :monthly-payment="item?.financing?.monthly_payment"
        :kasko="item?.financing?.kasko"
        :tooltip="item?.financing?.tooltip"
        :specs="item.status_car === 'stock' ? item?.specs : {}"
        :is-stock-car="item.status_car === 'stock'"
        :as-heading="asHeading"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.cars {
  &__items {
    $parent: &;
    align-items: start;
    display: grid;
    flex-direction: column;
    &--default {
      gap: 56px;
      @include media-breakpoint-up(sm) {
        column-gap: 16px;
        grid-template-columns: repeat(2, 1fr);
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: repeat(3, 1fr);
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 16);
        @include responsive-spacing(row-gap, 56);
      }
      &:deep(.car-item) {
        height: 100%;
        position: relative;
        padding-top: 56px;
        &:before {
          background-color: $divider;
          content: '';
          height: 1px;
          left: 0;
          position: absolute;
          top: 0px;
          right: 0;
          width: 100%;
          display: none;
        }
        &:first-child {
          padding-top: 0;
          &:before {
            display: none;
          }
        }
        @include media-breakpoint-up(sm) {
          &:before {
            width: calc(100% + 8px);
          }
          &:nth-child(2n + 2) {
            &:before {
              left: -8px;
            }
          }
          &:nth-child(2) {
            padding-top: 0;
            &:before {
              display: none;
            }
          }
        }
        @include media-breakpoint-up(md) {
          &:nth-child(2n + 2) {
            &:before {
              left: 0px;
            }
          }
          &:nth-child(3n + 2) {
            &:before {
              width: calc(100% + 16px);
            }
          }
          &:nth-child(3n + 2),
          &:nth-child(3n + 3) {
            &:before {
              left: -8px;
            }
          }
          &:nth-child(3) {
            padding-top: 0;
            &:before {
              display: none;
            }
          }
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 56);
          &:first-child,
          &:nth-child(2),
          &:nth-child(3) {
            padding-top: 0;
          }
          &:before {
            width: calc(100% + px-to-vw(8));
          }
          &:nth-child(3n + 2) {
            &:before {
              width: calc(100% + px-to-vw(16));
            }
          }
          &:nth-child(3n + 2),
          &:nth-child(3n + 3) {
            &:before {
              @include responsive-spacing(left, -8);
            }
          }
        }
      }
    }
    &--compact {
      column-gap: 16px;
      row-gap: 24px;
      padding: 0 24px;
      grid-template-columns: repeat(4, 1fr);
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 16);
        @include responsive-spacing(row-gap, 24);
        @include responsive-spacing(padding-left, 24);
        @include responsive-spacing(padding-right, 24);
      }
      &:deep(.car-item) {
        position: relative;
        padding-top: 24px;
        &:before {
          background-color: $divider;
          content: '';
          height: 1px;
          left: -8px;
          position: absolute;
          top: 0px;
          right: 0;
          width: calc(100% + 16px);
        }
        &:first-child,
        &:nth-child(2),
        &:nth-child(3),
        &:nth-child(4) {
          padding-top: 0;
          &:before {
            display: none;
          }
        }
        &:nth-child(4n + 1) {
          &:before {
            left: -24px;
            width: calc(100% + 40px);
          }
        }
        &:nth-child(4n + 4) {
          &:before {
            width: calc(100% + 32px);
          }
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 24);
          &:before {
            @include responsive-spacing(height, 1);
            @include responsive-spacing(left, -8);
            width: calc(100% + px-to-vw(16));
          }
          &:nth-child(4n + 1) {
            &:before {
              @include responsive-spacing(left, -24);
              width: calc(100% + px-to-vw(40));
            }
          }
          &:nth-child(4n + 4) {
            &:before {
              width: calc(100% + px-to-vw(32));
            }
          }
        }
      }
    }
  }
}
</style>
